import axios from "axios";
import React, { useEffect, useState } from "react";
import { Modal } from "react-bootstrap";
import { toast } from "react-toastify";
import { API_URL, TOKEN } from "../../../variables/constants";

const AddTypesPieces = ({
  show,
  handleClose,
  editMode,
  selectedTypePiece,
  onAddSuccess,
  onUpdateSuccess,
  existingTypePieces,
}) => {
  const [loading, setLoading] = useState(false);

  const [nom, setNom] = useState("");
  const [commentaire, setCommentaire] = useState("");
  const [validite, setValidite] = useState(""); // jours (optionnel)
  const [obligatoire, setObligatoire] = useState(true);
  const [pieceSinistre, setPieceSinistre] = useState(true);

  useEffect(() => {
    if (editMode && selectedTypePiece) {
      setNom(selectedTypePiece.nom ?? "");
      setCommentaire(selectedTypePiece.commentaire ?? "");
      setValidite(
        selectedTypePiece.validite !== null && selectedTypePiece.validite !== undefined
          ? String(selectedTypePiece.validite)
          : ""
      );
      setObligatoire(Boolean(selectedTypePiece.obligatoire));
      setPieceSinistre(Boolean(selectedTypePiece.pieceSinistre));
    } else {
      setNom("");
      setCommentaire("");
      setValidite("");
      setObligatoire(true);
      setPieceSinistre(true);
    }
  }, [show, editMode, selectedTypePiece]);

  const onSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    const trimmedNom = String(nom).trim();
    if (!trimmedNom) {
      toast.warn("Le nom de la pièce est obligatoire.");
      setLoading(false);
      return;
    }
    if (validite !== "" && Number(validite) <= 0) {
      toast.warn("La validité (en jours) doit être supérieure à 0.");
      setLoading(false);
      return;
    }

    // Vérif doublon côté client (nom + flags principaux)
    const lower = trimmedNom.toLowerCase();
    const isDuplicate = existingTypePieces.some((it) => {
      const sameName = String(it.nom || "").trim().toLowerCase() === lower;
      if (!editMode) return sameName && Boolean(it.obligatoire) === obligatoire && Boolean(it.pieceSinistre) === pieceSinistre;
      // en édition: exclure l’élément courant
      return (
        it.typePieceId !== selectedTypePiece?.typePieceId &&
        sameName &&
        Boolean(it.obligatoire) === obligatoire &&
        Boolean(it.pieceSinistre) === pieceSinistre
      );
    });
    if (isDuplicate) {
      toast.warn("Un type de pièce avec ce nom et ces attributs existe déjà.");
      setLoading(false);
      return;
    }

    const payload = {
      ...(editMode ? { typePieceId: selectedTypePiece.typePieceId } : {}),
      nom: trimmedNom,
      commentaire: commentaire?.trim() || null,
      validite: validite === "" ? null : Number(validite),
      obligatoire: Boolean(obligatoire),
      pieceSinistre: Boolean(pieceSinistre),
    };

    try {
      const headers = {
        Authorization: `Bearer ${TOKEN}`,
        "Content-Type": "application/json",
      };

      const response = editMode
        ? await axios.put(
            `${API_URL}/api/type-piece/${selectedTypePiece.typePieceId}`,
            payload,
            { headers }
          )
        : await axios.post(`${API_URL}/api/type-piece`, payload, { headers });

      toast.success(editMode ? "Type de pièce modifié avec succès" : "Type de pièce ajouté avec succès");

      if (editMode) {
        onUpdateSuccess(response.data);
      } else {
        onAddSuccess(response.data);
      }
      handleClose();
    } catch (err) {
      console.error(err);
      if (err.response && err.response.status === 409) {
        toast.error("Conflit : ce type de pièce existe déjà sur le serveur.");
      } else if (err.response && err.response.status === 400) {
        toast.error(err.response.data?.message || "Requête invalide.");
      } else {
        toast.error("Erreur lors de l'enregistrement du type de pièce.");
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal show={show} onHide={handleClose} size="lg" centered>
      <Modal.Body>
        <button
          type="button"
          className="btn-close btn-primary"
          onClick={handleClose}
          aria-label="Close"
        ></button>

        <div className="text-center mb-4">
          <h4 className="address-title mb-2">
            {editMode ? "Modifier un type de pièce" : "Ajouter un type de pièce"}
          </h4>
          <p className="address-subtitle">
            {editMode
              ? "Modifier le type de pièce existant"
              : "Ajouter un nouveau type de pièce"}
          </p>
        </div>

        <form className="row g-3" onSubmit={onSubmit}>
          <div className="col-md-6 col-12">
            <label className="form-label" htmlFor="tpNom">Nom *</label>
            <input
              id="tpNom"
              type="text"
              className="form-control"
              value={nom}
              onChange={(e) => setNom(e.target.value)}
              placeholder="Ex: Acte de décès"
              required
            />
          </div>

          <div className="col-md-6 col-12">
            <label className="form-label" htmlFor="tpValidite">Validité (jours)</label>
            <input
              id="tpValidite"
              type="number"
              className="form-control"
              value={validite}
              onChange={(e) => setValidite(e.target.value)}
              placeholder="Ex: 90 (laisser vide si non applicable)"
              min="1"
            />
          </div>

          <div className="col-12">
            <label className="form-label" htmlFor="tpComment">Commentaire</label>
            <textarea
              id="tpComment"
              className="form-control"
              rows={3}
              value={commentaire}
              onChange={(e) => setCommentaire(e.target.value)}
              placeholder="Note interne / précision..."
            />
          </div>

          <div className="col-md-6 col-12 d-flex align-items-center gap-2">
            <input
              id="tpObligatoire"
              type="checkbox"
              className="form-check-input"
              checked={obligatoire}
              onChange={(e) => setObligatoire(e.target.checked)}
            />
            <label className="form-check-label" htmlFor="tpObligatoire">
              Obligatoire par défaut
            </label>
          </div>

          <div className="col-md-6 col-12 d-flex align-items-center gap-2">
            <input
              id="tpSinistre"
              type="checkbox"
              className="form-check-input"
              checked={pieceSinistre}
              onChange={(e) => setPieceSinistre(e.target.checked)}
            />
            <label className="form-check-label" htmlFor="tpSinistre">
              Pièce rattachée à un sinistre
            </label>
          </div>

          <div className="col-12 text-center">
            <button type="submit" className="btn btn-primary me-3" disabled={loading}>
              {loading ? (
                <>
                  <span className="spinner-grow spinner-grow-sm me-1" role="status" aria-hidden="true"></span>
                  Enregistrement...
                </>
              ) : (
                "Enregistrer"
              )}
            </button>
            <button type="button" className="btn btn-label-secondary" onClick={handleClose}>
              Annuler
            </button>
          </div>
        </form>
      </Modal.Body>
    </Modal>
  );
};

export default AddTypesPieces;
